import { closeChecklistURLs } from 'routes/close/closeURLs';

import { CommentNotificationDisplay } from 'components/comments/CommentNotificationDisplay';
import { UserAvatar } from 'components/data-display/UserAvatar';

import { useUsersData } from 'hooks/useUsersData';

import { getName } from 'utils/users/config';

import { GenericNotificationItem } from 'layout/inbox-notifications/GenericNotificationItem';
import {
  CommentReplyNotificationItem,
  type TaskCommentReplyNotification,
} from 'layout/inbox-notifications/close/checklist/task/comment/CommentReplyNotificationItem';

import {
  TaskCommentAddedNotification,
  TaskCommentEditedNotification,
  TaskCommentTaggedNotification,
} from 'types/inbox-notifications/close/ChecklistNotifications';
import { SegmentAtByDot } from 'types/utils';

export type TaskCommentNotification =
  | TaskCommentAddedNotification
  | TaskCommentTaggedNotification
  | TaskCommentEditedNotification
  | TaskCommentReplyNotification;
type TaskCommentLevelKeys = SegmentAtByDot<TaskCommentNotification['type'], 4>;

export function CommentNotificationItem({
  type,
  item,
}: {
  type: string[];
  item: TaskCommentNotification;
}) {
  const [actionType, ...restOfType] = type;
  if (!item.task || !item.comment) {
    return <div>Notification task not found</div>;
  }

  switch (actionType as TaskCommentLevelKeys) {
    case 'added':
      return (
        <AddedNotificationItem item={item as TaskCommentAddedNotification} />
      );
    case 'tagged':
      return (
        <TaggedNotificationItem item={item as TaskCommentTaggedNotification} />
      );
    case 'edited':
      return (
        <EditedNotificationItem item={item as TaskCommentEditedNotification} />
      );
    case 'reply':
      return (
        <CommentReplyNotificationItem
          item={item as TaskCommentReplyNotification}
          type={restOfType}
        />
      );
    default:
      return <div>Unknown task comment notification type: {actionType}</div>;
  }
}

function AddedNotificationItem({
  item,
}: {
  item: TaskCommentAddedNotification;
}) {
  const { usersMap } = useUsersData();
  return (
    <GenericNotificationItem
      item={item}
      url={closeChecklistURLs.taskSingleComment({
        taskId: item.task.core_snapshot.short_code,
        commentId: item.comment.id,
      })}
      avatar={<UserAvatar userId={item.actor_id} />}
      title={`${getName(item.actor_id, usersMap)} added a comment.`}
      description={<CommentNotificationDisplay comment={item.comment} />}
    />
  );
}

function TaggedNotificationItem({
  item,
}: {
  item: TaskCommentTaggedNotification;
}) {
  const { usersMap } = useUsersData();
  return (
    <GenericNotificationItem
      item={item}
      url={closeChecklistURLs.taskSingleComment({
        taskId: item.task.core_snapshot.short_code,
        commentId: item.comment.id,
      })}
      avatar={<UserAvatar userId={item.actor_id} />}
      title={`${getName(item.actor_id, usersMap)} tagged you in a comment.`}
      description={<CommentNotificationDisplay comment={item.comment} />}
    />
  );
}

function EditedNotificationItem({
  item,
}: {
  item: TaskCommentEditedNotification;
}) {
  const { usersMap } = useUsersData();
  return (
    <GenericNotificationItem
      item={item}
      url={closeChecklistURLs.taskSingleComment({
        taskId: item.task.core_snapshot.short_code,
        commentId: item.comment.id,
      })}
      avatar={<UserAvatar userId={item.actor_id} />}
      title={`${getName(item.actor_id, usersMap)} updated a comment.`}
      description={<CommentNotificationDisplay comment={item.comment} />}
    />
  );
}
